import * as React from "react";
import * as L from "leaflet";
import { IBoundLayer, IBoundLayerOptions } from "../../../../map/BoundLayer";
import { IFlightMarkersState } from "../../../../redux/modules/flightMarkers/flightMarkersReducer";
import { IDeprecatedUIState } from "../../../../redux/modules/ui/reducer";
import {
  getDeprecatedUIState,
  getFlightMarkersState,
  getGCPRequestState,
  getModeGcpRequestUiState,
  getSiteState,
} from "../../../../redux/rootReducer";
import { attachPopup, attachReactPopup } from "../../../../map/popup";
import { getNamedGcpRequestSuggestedFlightMarkers } from "../../../../redux/computables/gcpRequest/namedSuggestedFlightMarkers";
import SuggestedFlightMarkerPopupContent from "../SuggestedFlightMarkerPopupContent";
import {
  getInstalledFlightMarkerById,
  isSupportedCorruptionStatus,
} from "../../../../redux/modules/gcpRequest/reducer";
import {
  userClosesFlightMarkerPopup,
  userMarksFlightMarkerAsInstalledV2,
  userMarksFlightMarkerAsNotInstalledV2,
  userSelectsActiveFMOnTheMap,
  userSelectsCompleteFMOnTheMap,
  userSelectsRequestedFMOnTheMap,
  userSelectsSuggestedFMOnTheMap,
} from "../scenarios";
import { IFlightMarker } from "../../../../apiFacades/bapi/flightMarkers";
import { convertGlobalToWgs84Coords } from "../../../../utils/coordinateConversion/convertGlobalToWgs84Coords";
import { getSiteSrs } from "../../../../redux/modules/site/reducer";
import currentGcpRequestStore from "../../currentGCPRequestStore";
import {
  GcpRequestInstruction,
  SuggestedGcpType,
} from "../../../../apiFacades/bapi/gcpRequests";
import { getCorruptedMarkerDescription } from "../../../../redux/computables/gcpDescriptions";
import { renderToString } from "react-dom/server";
import RemeasureIcon from "../../../icons/RemeasureIcon";
import { GcpMapIcon } from "../../../../redux/computables/flightMarkerIcon/GcpMapIcon";
import { AnnotatableIconContainer } from "../AnnotatableIconContainer";
import { getCorruptedIconColors } from "../../../../redux/computables/flightMarkerIcon/flightMarkerIcon";

const currentGcpRequestStoreState = currentGcpRequestStore.getState();

export default class NailsLayer extends L.LayerGroup implements IBoundLayer {
  public options: IBoundLayerOptions;
  public map: L.Map;

  // HACK: InitialGcpRequestViewPage has auto update logic and when it updates flight markers popup is closed
  private updateIsBlocked: boolean = false;

  constructor(options: IBoundLayerOptions) {
    super();
    L.Util.setOptions(this, options);
  }

  public onAdd(map: L.Map) {
    L.LayerGroup.prototype.onAdd.call(this, map);
    this.map = map;
    return this;
  }

  public onRemove(map: L.Map) {
    L.LayerGroup.prototype.onRemove.call(this, map);
    this.map = null;
    return this;
  }

  public update() {
    if (this.updateIsBlocked) {
      return;
    }

    this.clearLayers();

    const nailsState: IFlightMarkersState = this.options.getState().nails;
    const {
      allIsVisible,
      existingIsVisible,
      problemIsVisible,
    }: IDeprecatedUIState = getDeprecatedUIState(this.options.getState());
    const corruptedFlightMarkers = getGCPRequestState(
      this.options.getState(),
    ).corruptedFlightMarkers;
    const nailsList = nailsState.data?.nails ?? [];

    const instructionsRecord =
      currentGcpRequestStore.getState().GCPRequest?.instructions;

    this.clearLayers();

    // FIXME: allIsVisible seems to only refer to "Active" section
    if (allIsVisible) {
      const allNailsList = nailsList.filter(
        ({ id, tsArchived }) =>
          !tsArchived &&
          !(
            corruptedFlightMarkers[id] ||
            Boolean(instructionsRecord?.[id]?.length)
          ),
      );
      this.renderFlightMarkers(allNailsList);
    }
    if (existingIsVisible) {
      this.renderExistingGcps();
    }
    if (problemIsVisible) {
      const corruptedNailsList = nailsList.filter(
        ({ id, tsArchived }) =>
          !tsArchived &&
          (corruptedFlightMarkers[id] ||
            Boolean(instructionsRecord?.[id]?.length)),
      );
      this.renderFlightMarkers(corruptedNailsList);
      this.renderSuggestedFlightMarkers();
    }
  }

  private renderExistingGcps() {
    const srs = getSiteSrs(getSiteState(this.options.getState()));
    const gcpRequestState = getGCPRequestState(this.options.getState());
    const { showFlightMarkerLabelsIsOn } = getModeGcpRequestUiState(
      this.options.getState(),
    );
    const { activeNailId } = getDeprecatedUIState(this.options.getState());
    const existingGcps = gcpRequestState.existingGcps;

    existingGcps.forEach((existingGcp) => {
      const coords = convertGlobalToWgs84Coords(existingGcp.coordinates, srs);
      const markerIcon = new L.DivIcon({
        html: renderToString(
          <GcpMapIcon
            type="existing"
            isExistingHighPrio={existingGcp.highPriority}
            isSelected={existingGcp.id === activeNailId}
          />,
        ),
        iconSize: [24, 24],
      });
      const markerCoords: L.LatLng = L.latLng({ lat: coords.y, lng: coords.x });
      const marker = L.marker(markerCoords, { icon: markerIcon });
      attachPopup(
        marker,
        `<span>${existingGcp.name}</span>`,
        () => {
          this.options.dispatch(userSelectsRequestedFMOnTheMap());
        },
        showFlightMarkerLabelsIsOn,
      );
      this.addLayer(marker);
      if (showFlightMarkerLabelsIsOn) {
        setTimeout(() => marker.openPopup(), 0);
      }
    });
  }

  private renderFlightMarkers(nails: IFlightMarker[]) {
    // TODO: reuse it in NailsLayer
    const srs = getSiteSrs(getSiteState(this.options.getState()));
    const { showFlightMarkerLabelsIsOn } = getModeGcpRequestUiState(
      this.options.getState(),
    );
    const instructionsRecord =
      currentGcpRequestStore.getState().GCPRequest?.instructions;

    nails.forEach(({ operatorCoords, name, id }: IFlightMarker) => {
      const { x, y } = convertGlobalToWgs84Coords(operatorCoords, srs);
      const markerCoords: L.LatLng = L.latLng(y, x);
      const corruptionType = currentGcpRequestStore
        .getState()
        .getCorruptionById(id)?.type;
      const isRemeasured = (instructionsRecord?.[id] ?? []).includes(
        GcpRequestInstruction.REMEASURE,
      );

      const markerIcon = new L.DivIcon({
        // in react docs is it is not recommended to use renderToString in prod
        // but firstly, we already in other apps, and secondly it's way better
        // than creating separate icon files for all color/position combinations
        html: renderToString(
          <AnnotatableIconContainer
            icon={<GcpMapIcon type="gcp" corruptionType={corruptionType} />}
            annotationIcon={
              isRemeasured && (
                <RemeasureIcon
                  color={getCorruptedIconColors(corruptionType).color}
                />
              )
            }
          />,
        ),
        iconSize: [24, 24],
        popupAnchor: [8, 12],
      });
      const marker = L.marker(markerCoords, { icon: markerIcon });
      // shadow marker just stores popup with name
      const shadowMarker = L.marker(markerCoords, { icon: markerIcon });

      if (isSupportedCorruptionStatus(corruptionType)) {
        attachPopup(
          marker,
          `<span style="padding-right: 0.5em">${name},</span>` +
            `<span style="color: #666666">${getCorruptedMarkerDescription(
              corruptionType,
            )}</span>`,
          () => {
            this.options.dispatch(userSelectsRequestedFMOnTheMap());
          },
        );
      } else {
        attachPopup(
          marker,
          `<span>${name}</span>`,
          () => {
            this.options.dispatch(userSelectsActiveFMOnTheMap());
          },
          showFlightMarkerLabelsIsOn,
        );
      }
      attachPopup(shadowMarker, name, null, showFlightMarkerLabelsIsOn);

      this.addLayer(shadowMarker);
      this.addLayer(marker);

      // should be after adding to the map
      if (showFlightMarkerLabelsIsOn) {
        setTimeout(() => shadowMarker.openPopup(), 0);
      }
    });
  }

  private renderSuggestedFlightMarkers() {
    const gcpRequestState = getGCPRequestState(this.options.getState());
    const namedSuggestedFlightMarkers =
      getNamedGcpRequestSuggestedFlightMarkers(
        gcpRequestState,
        getFlightMarkersState(this.options.getState()),
      );
    const { showFlightMarkerLabelsIsOn } = getModeGcpRequestUiState(
      this.options.getState(),
    );

    namedSuggestedFlightMarkers.forEach(({ name, flightMarker }) => {
      const { id, coordinates } = flightMarker;
      const isInstalled = !!getInstalledFlightMarkerById(gcpRequestState, id);

      const suggestedGCPType =
        currentGcpRequestStoreState.getSuggestedGcpById(id)?.type;
      let iconUrl = "icon-gcp.svg";

      if (!isInstalled) {
        if (suggestedGCPType === SuggestedGcpType.SEND_COORDINATES) {
          iconUrl = "icon-gcp-white-dashed.svg";
        } else {
          iconUrl = "icon-gcp-white.svg";
        }
      }

      const markerIcon = L.icon({
        iconUrl,
        iconSize: [24, 24],
        // this.options.zIndex is ignored because in leaflet markers are always on top
      });

      const markerCoords: L.LatLng = L.latLng(coordinates);
      const marker = L.marker(markerCoords, { icon: markerIcon });
      // shadow marker just stores popup with name
      const shadowMarker = L.marker(markerCoords, { icon: markerIcon });

      attachReactPopup(
        marker,
        (updatePopupLayout, closePopup) => (
          <SuggestedFlightMarkerPopupContent
            name={name}
            isInstalled={isInstalled}
            updatePopupLayout={updatePopupLayout}
            closePopup={() => {
              closePopup();
              this.options.dispatch(userClosesFlightMarkerPopup());
            }}
            onInstallAction={() => {
              closePopup();
              if (isInstalled) {
                this.options.dispatch(
                  userMarksFlightMarkerAsNotInstalledV2(id),
                );
              } else {
                this.options.dispatch(userMarksFlightMarkerAsInstalledV2(id));
              }
            }}
          />
        ),
        () => {
          if (isInstalled) {
            this.options.dispatch(userSelectsCompleteFMOnTheMap());
          } else {
            this.options.dispatch(userSelectsSuggestedFMOnTheMap());
          }
          this.updateIsBlocked = true;
        },
        () => {
          this.updateIsBlocked = false;
        },
      );
      attachPopup(shadowMarker, name, null, showFlightMarkerLabelsIsOn);

      this.addLayer(shadowMarker);
      this.addLayer(marker);

      if (showFlightMarkerLabelsIsOn) {
        setTimeout(() => shadowMarker.openPopup(), 0);
      }
    });
  }
}
